import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import {
  CreateUserRequestSchema,
  CustomerRoleSchema,
  type CreateUserRequest,
  type Organisation,
} from "@thalamos/haven-common";

import { CancelOrProceedActions } from "../CancelOrProceedActions/CancelOrProceedActions.js";
import {
  OrganisationPicker,
  type OrganisationSearch,
} from "../OrganisationPicker/OrganisationPicker.js";

/**
 * Whether the account was created. `exists` is told apart because the person
 * can act on it — by adding that account instead — and nothing they could do
 * tells any other refusal from an unreachable server.
 */
export type CreateUserOutcome = "created" | "exists" | "failed";

type Status = "creating" | "exists" | "adding" | "failed";

/**
 * Asks for a new account's name, address and first membership, and creates it.
 *
 * Checked against the schema the server parses with, so the button only offers
 * a request the server's validation will accept.
 */
export const CreateUserDialog = ({
  open,
  organisations,
  organisationSearch,
  onClose,
  onCreate,
}: {
  open: boolean;
  /** The organisations a membership may be given at. */
  organisations: readonly Organisation[];
  organisationSearch?: OrganisationSearch | undefined;
  onClose: () => Promisable<void>;
  onCreate: (request: CreateUserRequest) => Promisable<CreateUserOutcome>;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [organisation, setOrganisation] = useState<Organisation | null>(null);
  const [role, setRole] = useState("");
  const [touched, setTouched] = useState({ name: false, email: false });
  const [status, setStatus] = useState<Status>();

  const parsed = CreateUserRequestSchema.safeParse({
    name,
    email,
    organisationId: organisation?.id,
    role,
  });
  const busy = status === "creating" || status === "adding";

  // The schema's messages are translation keys, so what comes back is
  // translated rather than rendered.
  const problemWith = (field: "name" | "email"): string | undefined => {
    const message = parsed.error?.issues.find(
      (issue) => issue.path[0] === field,
    )?.message;
    return touched[field] && message !== undefined ? t(message) : undefined;
  };
  const nameProblem = problemWith("name");
  const emailProblem = problemWith("email");

  // Any edit makes an earlier answer one about a different request.
  const edited = () => {
    setStatus(undefined);
  };

  const reset = () => {
    setName("");
    setEmail("");
    setOrganisation(null);
    setRole("");
    setTouched({ name: false, email: false });
    setStatus(undefined);
  };

  const close = () => {
    reset();
    void onClose();
  };

  const submit = async (addToExistingAccount: boolean) => {
    if (!parsed.success) {
      return;
    }

    setStatus(addToExistingAccount ? "adding" : "creating");
    let outcome: CreateUserOutcome;

    try {
      outcome = await onCreate(
        addToExistingAccount
          ? { ...parsed.data, addToExistingAccount }
          : parsed.data,
      );
    } catch {
      outcome = "failed";
    }

    if (outcome === "created") {
      reset();
      return;
    }

    setStatus(outcome);
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} fullWidth>
      <DialogTitle>{t("admin.users.create.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            fullWidth
            required
            label={t("admin.users.create.name")}
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              edited();
            }}
            onBlur={() => {
              setTouched((was) => ({ ...was, name: true }));
            }}
            error={nameProblem !== undefined}
            helperText={nameProblem}
          />
          <TextField
            fullWidth
            required
            type="email"
            label={t("admin.users.create.email.label")}
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              edited();
            }}
            onBlur={() => {
              setTouched((was) => ({ ...was, email: true }));
            }}
            error={emailProblem !== undefined}
            helperText={emailProblem ?? t("admin.users.create.email.help")}
          />
          <OrganisationPicker
            label={t("admin.users.create.organisation")}
            organisations={organisations}
            search={organisationSearch}
            value={organisation}
            onChange={(chosen) => {
              setOrganisation(chosen);
              edited();
            }}
            required
          />
          <TextField
            select
            fullWidth
            required
            size="small"
            label={t("admin.users.create.role")}
            value={role}
            onChange={(event) => {
              setRole(event.target.value);
              edited();
            }}
          >
            {CustomerRoleSchema.options.map((each) => (
              <MenuItem key={each} value={each}>
                {t(`roles.${each}`)}
              </MenuItem>
            ))}
          </TextField>

          {status === "exists" || status === "adding" ? (
            <Alert
              severity="info"
              action={
                <Button
                  color="inherit"
                  size="small"
                  disabled={busy}
                  onClick={() => {
                    void submit(true);
                  }}
                >
                  {t("admin.users.create.addToOrganisation")}
                </Button>
              }
            >
              {t("admin.users.create.exists")}
            </Alert>
          ) : null}
          {status === "failed" ? (
            <Alert severity="error">{t("admin.users.create.failed")}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <CancelOrProceedActions
        cancel={{
          onClick: close,
          disabled: busy,
        }}
        proceed={{
          label: t("admin.users.create.submit"),
          onClick: () => submit(false),
          disabled: !parsed.success || busy || status === "exists",
          kind: "primary",
        }}
      />
    </Dialog>
  );
};
